import React, { useState, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Spinner } from "@chakra-ui/react";
import { FiDownload, FiInfo } from "react-icons/fi";
import { Modal } from "newui/components/ui/Modal";
import { ColumnSortHeader, type SortDirection } from "newui/components/TableComponents/ColumnSortHeader/ColumnSortHeader";
import { apiV1 } from "api-v2";
import { getKeyPersonnelDetail } from "newui/features/Company/company-api";
import { ViewProjectDetailDrawer } from "newui/features/Company/CompanyProjects/ViewProjectDetailDrawer";
import type { ViewProjectDetailData } from "newui/features/Company/CompanyProjects/ViewProjectDetail";
import type { KeyPersonnelProfileViewItem } from "newui/features/Company/types/KeyPersonnelProfileView.type";
import type { KeyPersonnelProject } from "../types/KeyPersonnel.type";
import { MobileKeyPersonnelProjectCard } from "newui/features/Company/CompanyProfile/Preview/components/mobile/MobileKeyPersonnelProjectCard";
import SectorPill from "newui/components/PillDisplay/SectorPill";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";

interface KeyPersonnelDetailModalProps {
	isOpen: boolean;
	person: KeyPersonnelProfileViewItem | null;
	profileTypeId: number;
	onClose: () => void;
}

type ProjectSortField = "name" | "role" | "sector" | "value";

export const KeyPersonnelDetailModal: React.FC<KeyPersonnelDetailModalProps> = ({
	isOpen,
	onClose,
	person,
	profileTypeId,
}) => {
	const [selectedProject, setSelectedProject] = useState<ViewProjectDetailData | null>(null);
	const [isProjectDrawerOpen, setIsProjectDrawerOpen] = useState(false);
	const [sortField, setSortField] = useState<ProjectSortField | null>(null);
	const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
	const [loadingProjectId, setLoadingProjectId] = useState<number | null>(null);

	const isConsultant = profileTypeId === 4;

	// Lazy-load the full key personnel detail (with projects) when modal opens
	const { data: fullDetail, isLoading: isLoadingDetail } = useQuery({
		queryKey: ["key-personnel-detail", person?.id],
		queryFn: () => getKeyPersonnelDetail(person!.id),
		enabled: isOpen && !!person,
		staleTime: 2 * 60 * 1000,
	});

	const handleSort = useCallback(
		(field: ProjectSortField) => {
			if (sortField === field) {
				setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
			} else {
				setSortField(field);
				setSortDirection("asc");
			}
		},
		[sortField],
	);

	const projects = useMemo(() => fullDetail?.projects_completed ?? [], [fullDetail?.projects_completed]);

	const sortedProjects = useMemo(() => {
		if (!sortField) return projects;
		return [...projects].sort((a, b) => {
			let cmp = 0;
			switch (sortField) {
				case "name":
					cmp = a.name.localeCompare(b.name);
					break;
				case "role":
					cmp = a.role.localeCompare(b.role);
					break;
				case "sector":
					cmp = (a.sectors?.[0]?.name ?? "").localeCompare(b.sectors?.[0]?.name ?? "");
					break;
				case "value":
					cmp = (parseFloat(a.value) || 0) - (parseFloat(b.value) || 0);
					break;
			}
			return sortDirection === "asc" ? cmp : -cmp;
		});
	}, [projects, sortField, sortDirection]);

	const handleProjectClick = useCallback(async (project: KeyPersonnelProject) => {
		if (project.isCompanyProject && project.project_id) {
			try {
				setLoadingProjectId(project.project_id);
				const { data } = await apiV1.get(`/past-projects/${project.project_id}`);
				const fullProject = data.data ?? data;
				setSelectedProject(fullProject);
			} catch {
				setSelectedProject({
					name: project.name,
					location: project.location,
					sectors: project.sectors,
					value_humanized: project.value_humanized,
				});
			} finally {
				setLoadingProjectId(null);
			}
		} else {
			setSelectedProject({
				name: project.name,
				location: project.location,
				sectors: project.sectors,
				value: parseFloat(project.value) || 0,
				value_humanized: project.value_humanized || project.value,
			});
		}
		setIsProjectDrawerOpen(true);
	}, []);

	const handleCloseProjectDrawer = useCallback(() => {
		setIsProjectDrawerOpen(false);
		setSelectedProject(null);
	}, []);

	if (!person) return null;

	const initials = `${person.first_name?.[0] ?? ""}${person.last_name?.[0] ?? ""}`.toUpperCase();
	const hasCompanyProjects = projects.some((p) => p.isCompanyProject);

	return (
		<>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title="Key Personnel Detail"
				iconType="info"
				size="6xl"
				closeOnEsc
				closeOnOverlayClick
			>
				<div className="flex flex-col gap-6">
					{/* Person Info Header */}
					<div className="flex items-start gap-5 rounded-lg border border-gray-200 bg-gray-50 p-5">
						<div
							className="flex flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-200"
							style={{ width: 80, height: 80 }}
						>
							{person.avatar_location ? (
								<img
									src={person.avatar_location}
									alt={person.full_name}
									className="h-full w-full object-cover"
								/>
							) : (
								<span className="text-lg font-semibold text-gray-500">
									{initials}
								</span>
							)}
						</div>
						<div className="flex flex-1 flex-col gap-2">
							<h3 className="text-xl font-semibold text-gray-900">
								{person.full_name}
							</h3>
							<p className="text-sm text-gray-600">{person.position}</p>
							<div className="flex flex-wrap gap-4 text-sm text-gray-500">
								{person.experience > 0 && (
									<span>{person.experience} years experience</span>
								)}
								{person.resume.length > 0 && (
									<a
										href={person.resume[0].file_path}
										target="_blank"
										rel="noopener noreferrer"
										className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700"
										onClick={(e) => e.stopPropagation()}
									>
										<FiDownload size={14} />
										Download Resume
									</a>
								)}
							</div>
						</div>
					</div>

					{/* Sectors */}
					{person.sectors.length > 0 && (
						<div>
							<h4 className="mb-2 text-sm font-semibold text-gray-700">
								Top 3 Sectors
							</h4>
							<div className="flex flex-wrap gap-2">
								{person.sectors.map((s) => (
									<SectorPill key={s.id} name={s.name} />
								))}
							</div>
						</div>
					)}

					{/* Expertise & Disciplines */}
					{isConsultant &&
						person.disciplinesFormatted &&
						person.disciplinesFormatted.length > 0 && (
							<div>
								<h4 className="mb-2 text-sm font-semibold text-gray-700">
									Expertise & Disciplines
								</h4>
								<div className="flex flex-col gap-2">
									{person.disciplinesFormatted.map((group) => (
										<div key={group.expertiseId} className="flex items-start gap-2">
											<span className="text-sm font-medium text-gray-800">
												{group.expertise}:
											</span>
											<div className="flex flex-wrap gap-1">
												{group.disciplines.map((d) => {
													const disc = person.disciplines?.find(
														(dd) => dd.id === d,
													);
													return (
														<span
															key={d}
															className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700"
														>
															{disc?.name ?? d}
														</span>
													);
												})}
											</div>
										</div>
									))}
								</div>
							</div>
						)}

					{/* Projects Table */}
					<div className="border-t bt-1 border-t-gray-300 pt-4">
						<h4 className="mb-3 text-sm font-semibold text-gray-700 flex flex-row items-center gap-2">
							Projects {!isLoadingDetail && ` (${projects.length})`}
							{hasCompanyProjects && <div className="flex items-center">- click <span className="text-blue-700 px-2"><FiInfo /></span> to view more details</div>}
						</h4>
						{isLoadingDetail ? (
							<div className="flex items-center justify-center py-8">
								<Spinner size="md" />
								<span className="ml-3 text-sm text-gray-500">
									Loading projects...
								</span>
							</div>
						) : projects.length === 0 ? (
							<p className="py-4 text-center text-sm text-gray-400">
								No projects associated with this person
							</p>
						) : (
							<>
								{/* Mobile: card list */}
								<div className="flex flex-col gap-3 md:hidden">
									{sortedProjects.map((project, idx) => (
										<MobileKeyPersonnelProjectCard
											key={`${project.name}-${idx}`}
											project={project}
											isLoading={loadingProjectId === project.project_id}
											onClick={
												project.isCompanyProject
													? () => handleProjectClick(project)
													: undefined
											}
										/>
									))}
								</div>

								{/* Desktop: table */}
								<div className="hidden overflow-x-auto rounded-lg border border-gray-200 md:block">
									<table className="w-full text-left text-sm">
										<thead className="border-b border-gray-200 bg-gray-50">
											<tr>
												<ColumnSortHeader<ProjectSortField>
													label="Project Name"
													field="name"
													activeField={sortField}
													direction={sortDirection}
													onSort={handleSort}
												/>
												<ColumnSortHeader<ProjectSortField>
													label="Role"
													field="role"
													activeField={sortField}
													direction={sortDirection}
													onSort={handleSort}
												/>
												<ColumnSortHeader<ProjectSortField>
													label="Sectors"
													field="sector"
													activeField={sortField}
													direction={sortDirection}
													onSort={handleSort}
												/>
												<ColumnSortHeader<ProjectSortField>
													label="Construction Value (AUD)"
													field="value"
													activeField={sortField}
													direction={sortDirection}
													onSort={handleSort}
												/>
												<th className="px-3 py-2 text-xs font-medium tracking-wide text-gray-500">
													Location
												</th>
											</tr>
										</thead>
										<tbody className="divide-y divide-gray-100">
											{sortedProjects.map((project, idx) => (
												<tr
													key={`${project.name}-${idx}`}
													className={`${project.isCompanyProject ? "cursor-pointer" : "cursor-default"} hover:bg-blue-50`}
													onClick={project.isCompanyProject ? () => handleProjectClick(project) : null}
													data-testid={`project-row-${idx}`}
												>
													<td className="px-3 py-3 font-medium text-gray-900">
														<div className="flex items-center gap-2">
															{project.name}
															{project.isCompanyProject && (
																<span className="rounded px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
																	{loadingProjectId === project.project_id ? <Spinner size="sm" /> : <FiInfo size={20} />}
																</span>
															)}
														</div>
													</td>
													<td className="px-3 py-3 text-gray-600">
														{project.role || "—"}
													</td>
													<td className="px-3 py-3 text-gray-600">
														{project.sectors?.length
															? project.sectors
																.map((s) => s.name)
																.join(", ")
															: project.sector?.name ?? "—"}
													</td>
													<td className="px-3 py-3 text-gray-600">
														{project.value_humanized ||
															project.value ||
															"—"}
													</td>
													<td className="px-3 py-3 text-gray-600">
														{project.location
															? typeof project.location === "string"
																? project.location
																: (project.location as any)?.value ?? "—"
															: "—"}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</>
						)}
					</div>
				</div>
			</Modal>

			<ViewProjectDetailDrawer
				isOpen={isProjectDrawerOpen}
				onClose={handleCloseProjectDrawer}
				project={selectedProject}
				profileTypeId={profileTypeId}
			/>
		</>
	);
};
